import React, { useRef, useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { ControlPanel } from '../components/ControlPanel'
import { PreviewStage } from '../components/PreviewStage'
import { CodePanel } from '../components/CodePanel'
import { useAnimationState } from '../hooks/useAnimationState'
import { useMotionAnimate } from '../hooks/useMotionAnimate'
import { useAnimationProgress } from '../hooks/useAnimationProgress'
import { decodeConfig } from '../utils/shareUrl'

export function EditorPage() {
  const targetRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const {
    state,
    setTransform,
    setTransitionType,
    setSpring,
    setTweenDuration,
    setTweenEase,
    setInertia,
    setTargetShape,
    play,
    stop,
    reset,
    loadConfig,
  } = useAnimationState()

  const { resetElement, animationRef } = useMotionAnimate(targetRef, state, stop)
  const progress = useAnimationProgress(animationRef, state.isPlaying)

  useEffect(() => {
    const config = (location.state as any)?.editorConfig
    if (config) {
      loadConfig(config)
      navigate('.', { replace: true, state: null })
      return
    }

    const params = new URLSearchParams(window.location.search)
    const configParam = params.get('config')
    if (configParam) {
      const decoded = decodeConfig(configParam)
      if (decoded) loadConfig(decoded)
    }
  }, [])

  const handleReset = useCallback(() => {
    resetElement()
    reset()
  }, [resetElement, reset])

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col bg-gray-50">
      <Header
        targetShape={state.targetShape}
        onShapeChange={setTargetShape}
        page="editor"
      />

      <div className="flex-1 flex overflow-hidden">
        <ControlPanel
          state={state}
          progress={progress}
          onTransformChange={setTransform}
          onTransitionTypeChange={setTransitionType}
          onSpringChange={setSpring}
          onTweenDurationChange={setTweenDuration}
          onTweenEaseChange={setTweenEase}
          onInertiaChange={setInertia}
          onPlay={play}
          onReset={handleReset}
        />

        <main className="flex-1 flex flex-col relative overflow-hidden">
          <PreviewStage ref={targetRef} targetShape={state.targetShape} />
          <CodePanel state={state} />
        </main>
      </div>

      <footer className="border-t border-gray-200 bg-white shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-xs text-gray-500 text-center">
            Built with{' '}
            <a
              href="https://motion.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline"
            >
              Motion
            </a>
            {' '}by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}
